import {Component, effect, inject, input, model, resource, viewChild} from '@angular/core';
import {WjGridFilterModule} from "@mescius/wijmo.angular2.grid.filter";
import {WjGridTransposedModule} from "@mescius/wijmo.angular2.grid.transposed";
import {WjFlexGrid, WjGridModule} from "@mescius/wijmo.angular2.grid";
import {Wijmo} from '../../lib/wjgrid-utils';
import {Stdlib} from '../../lib/stdlib';
import {AccountApi, AccountData, AccountDataSnapshot} from '../../../api-client-kt/src';
import {DynamicComponentBase} from '../../service/ng-dynamic-components.service';
import {FlexGrid, FormatItemEventArgs} from '@mescius/wijmo.grid';
import {CollectionView} from '@mescius/wijmo';
import {WjGridColumnChooser} from '../../lib/wjgrid-columchooser';
import {AccountGridService} from '../../views/account-grid/account-grid-helper';
import {orderBy} from 'es-toolkit';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';

interface AccountDataWithMods extends AccountData {
    mods: { [key: string]: boolean };
}

@Component({
    selector: 'app-account-history-grid',
    standalone: true,
    imports: [
        WjGridTransposedModule,
        WjGridFilterModule,
        WjGridModule
    ],
    templateUrl: './account-history-grid.component.html',
    styleUrl: './account-history-grid.component.scss'
})
export class AccountHistoryGridComponent extends DynamicComponentBase {

    protected readonly Wijmo = Wijmo;

    public readonly accountId = model<string | undefined>(undefined);
    readonly pfSegDataCtx = input<PortfolioSegmentDataContext>();

    public readonly accountHistory = resource({
        params: () => this.accountId(),
        loader: ({params}) => this.fetchAccountHistory(params)
    });

    private readonly accountApi = inject(AccountApi);
    private readonly grid = viewChild<WjFlexGrid>('grid');

    constructor() {
        super();
        effect(() => this.updateItemSource(this.accountHistory.value()));
    }

    async onGridInitialized(grid: FlexGrid) {
        const columnGroups = AccountGridService.getStandardViewConfiguration();
        await AccountGridService.populateKpiColumns(columnGroups, this.pfSegDataCtx()!);
        grid.columnGroups = columnGroups;
        const gridColumnGroups = grid.getColumnGroups();
        gridColumnGroups.forEach(group => group.isCollapsed = true);
        gridColumnGroups.unshift(gridColumnGroups.pop());
        gridColumnGroups[0].isCollapsed = false;

        WjGridColumnChooser.addColumnChooser('accountHistoryGrid', grid);
        grid.formatItem.addHandler(this.gridCellFormatHandler.bind(this));
        const columnHeaderRows = grid.columnHeaders.rows;
        columnHeaderRows[columnHeaderRows.length - 1].height = 80;
    }

    private gridCellFormatHandler(s: FlexGrid, e: FormatItemEventArgs) {
        const cell = e.cell;

        const col = s.columns[e.col];
        const row = s.rows[e.row];

        if (e.panel === s.cells) {
            const column = e.getColumn();
            const binding = col.binding!;
            const item = s.collectionView.items[e.row] as AccountDataWithMods;
            if (!item) return;

            if (item.mods && item.mods[binding]) {
                cell.classList.toggle('has-mod', true);
            }
        }
    }

    private async fetchAccountHistory(accountId: string | undefined): Promise<AccountDataSnapshot[]> {
        if (!accountId) return [];
        const historyRaw = await this.accountApi.getAccountSnapshots({ id: accountId });
        const history = orderBy(historyRaw, [ i => i.account.version ?? -1 ], ['desc']);
        for (let i = 0; i < history.length - 1; i++) {
            this.detectAttributeChanges(history[i], history[i + 1]);
        }
        return history;
    }

    private detectAttributeChanges(current: AccountDataSnapshot, previous: AccountDataSnapshot) {
        const currentAcct = current.account as any;
        const previousAcct = previous.account as any;

        currentAcct.mods = {} as any;
        for (const key in currentAcct) {
            if (!this.isEqual(currentAcct[key], previousAcct[key])) {
                currentAcct.mods[key] = true;
            }
        }
    }

    private isEqual(v1: any, v2: any): boolean {
        if (v1 === v2) return true;
        if (v1 == null) return v2 == null;
        if (v2 == null) return false;

        if (v1 instanceof Date) return v1.getTime() === v2.getTime();
        else return v1 == v2;
    }

    private updateItemSource(value: AccountDataSnapshot[] | undefined) {
        if (value == undefined) return;

        Stdlib.let(this.grid(), grid => {

            const cv = new CollectionView(value.map(value => {
                return {
                    ... value.account,
                    // version: value.revisionNumber,
                    updatedAt: value.updatedAt,
                    updatedBy: value.updatedBy
                };
            }));
            grid.itemsSource = cv;
        });
    }
}
